import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";

// Formik
import { Form } from "formik";

// Material
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import IconButton from "@material-ui/core/IconButton";

// Actions
import { parseFetchOptions } from "../../state/user/actions";
import { updateCaseIncident } from "../../state/incident/actions";

// Config
import { getRoute, urls } from "../../config/routes";
import { getShareWithRecipientsUrl } from "../../config/apiConfig";
import colors from "../../config/colors";
import styles from "../../config/styles";

// Icons
import closeIcon from "../../assets/icons/close-24px.svg";
import gpsIcon from "../../assets/icons/gps.svg";

// Hooks
import { useAppInsights } from "../../utils/telemetry/AppInsights";
import useTranslate from "../../hooks/useTranslate";

// Handlers
import { useShareWith } from "./ShareWithProvider";
import LocationHandler from "../CaseIncidentsSubmit/LocationHandler";

// Components
import ContentCardHeader from "../../components/ContentCardHeader";
import IncidentTypeField from "../../components/Input/IncidentTypeField";
import Link from "../../components/Link";
import LoadingSection from "../../components/LoadingSection";
import LocationField from "../../components/Input/LocationField";
import ShareWithOptionList from "./ShareWithOptionList";
import Text from "../../components/Text";

const useStyles = makeStyles(theme => ({
  root: {
    border: styles.values.border,
    marginBottom: theme.spacing(2),
    overflow: "hidden"
  },
  rootIntel: {
    borderColor: colors.royalBlue
  },
  cardTitle: {
    textTransform: "capitalize"
  },
  cardTitleIntel: {
    color: colors.deepKoamaru
  },
  contentRoot: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2)
  },
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2, 3)
  },
  label: {
    marginBottom: theme.spacing(1.5)
  },
  placeholderWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2),
    maxWidth: 350,
    margin: "0 auto",
    padding: theme.spacing(4)
  },
  placeholderIcon: {
    height: 76,
    width: 76
  },
  placeholderText: {
    lineHeight: 1.5,
    textAlign: "center"
  }
}));

const useLocationWatcher = (location, coord, isDirty, onChange) => {
  const prevLocation = useRef(location);
  const prevCoord = useRef(coord);

  useEffect(() => {
    if (!isDirty) return;

    const locationChanged = prevLocation.current !== location;
    const coordChanged = prevCoord.current !== coord;

    if (locationChanged || coordChanged) {
      onChange(location, coord);
    }

    prevLocation.current = location;
    prevCoord.current = coord;
  }, [location, coord, isDirty, onChange]);
};

const useValueWatcher = (value, onChange) => {
  const prevValue = useRef(value);

  useEffect(() => {
    const valueChanged = prevValue.current !== value;

    if (valueChanged) {
      onChange(value);
    }

    prevValue.current = value;
  }, [value, onChange]);
};

const ShareWithFormHandler = ({
  coord,
  errors,
  setCoord,
  setFieldValue,
  values: { location, incidentTypeId }
}) => {
  const dispatch = useDispatch();
  const { submitted, isIntel } = useShareWith();

  const classes = useStyles();
  const { caseId } = useParams();
  const getText = useTranslate();

  const [isDirty, setIsDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const { trackException } = useAppInsights();

  const loadRecipients = () => {
    if (location && incidentTypeId) {
      dispatch(
        parseFetchOptions(({ headers }) => {
          fetch(getShareWithRecipientsUrl(caseId), { headers })
            .then(res => res.json())
            .then(result => {
              setData(result);
            })
            .catch(() => {
              trackException(
                `Failed to get share with recipients from case ${caseId}`
              );
            })
            .finally(() => {
              setLoading(false);
            });
        })
      );
    } else {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (location && incidentTypeId) {
      setLoading(true);
      loadRecipients();
    }
  }, []);

  useLocationWatcher(location, coord, isDirty, () => {
    if (!location || !coord) return;

    setLoading(true);

    const sentData = {
      location: {
        name: location,
        coordinate: {
          latitude: coord.lat ? coord.lat() : coord.latitude,
          longitude: coord.lng ? coord.lng() : coord.longitude
        }
      }
    };

    dispatch(
      updateCaseIncident({
        onFinish: loadRecipients,
        caseId,
        incident: sentData
      })
    );
  });

  useValueWatcher(incidentTypeId?.value, () => {
    setLoading(true);

    const sentData = {
      incidentTypeId: incidentTypeId?.value
    };

    dispatch(
      updateCaseIncident({
        onFinish: loadRecipients,
        caseId,
        incident: sentData
      })
    );
  });

  let renderContent = null;

  if (loading) {
    renderContent = <LoadingSection normalHeight noBackground />;
  } else if (!location) {
    renderContent = (
      <div className={classes.placeholderWrapper}>
        <img className={classes.placeholderIcon} src={gpsIcon} alt="location" />
        <Text
          className={classes.placeholderText}
          text="caseDetailPage.missingLocationDialog.title"
          type="heading3"
        />
        <Text
          className={classes.placeholderText}
          text="caseDetailPage.missingLocationDialog.description"
          type="contentLabel3"
        />
      </div>
    );
  }

  const handleSetCoord = value => {
    if (!submitted) {
      setIsDirty(true);
      setCoord(value);
    }
  };

  return (
    <Form className={classes.contentRoot}>
      <Paper
        elevation={0}
        className={clsx(classes.root, {
          [classes.rootIntel]: isIntel
        })}
      >
        <ContentCardHeader>
          <Text
            className={clsx(classes.cardTitle, {
              [classes.cardTitleIntel]: isIntel
            })}
            type="heading2"
            text={
              isIntel
                ? "common.prompts.shareAnonymouslyWith"
                : "common.prompts.shareWith"
            }
          />
          <IconButton
            component={Link}
            to={getRoute(urls.caseDetail, { caseId })}
          >
            <img className={classes.closeIcon} src={closeIcon} alt="close" />
          </IconButton>
        </ContentCardHeader>
        <div className={classes.contentContainer}>
          <IncidentTypeField
            error={errors.incidentTypeId}
            label={getText("caseIncidentsPage.incidentTypeLabel")}
            name="incidentTypeId"
            disabled={!!incidentTypeId && submitted}
          />
          <LocationHandler
            location={location}
            coord={coord}
            setCoord={setCoord}
          />
          <LocationField
            error={errors.location}
            label={getText("caseIncidentsPage.locationLabel")}
            name="location"
            placeholder={getText("caseIncidentsPage.locationPlaceholder")}
            setCoord={handleSetCoord}
            setFieldValue={setFieldValue}
            disabled={submitted}
          />
          {renderContent || (
            <ShareWithOptionList
              options={data}
              isLoading={loading}
              showPlaceholder={!location}
            />
          )}
        </div>
      </Paper>
    </Form>
  );
};

ShareWithFormHandler.propTypes = {
  coord: PropTypes.shape({
    lat: PropTypes.func,
    lng: PropTypes.func,
    longitude: PropTypes.number,
    latitude: PropTypes.number
  }),
  errors: PropTypes.shape({
    location: PropTypes.string,
    incidentTypeId: PropTypes.string
  }),
  setCoord: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  values: PropTypes.shape({
    location: PropTypes.string,
    incidentTypeId: PropTypes.shape({
      value: PropTypes.string
    })
  }).isRequired
};

ShareWithFormHandler.defaultProps = {
  coord: {},
  errors: {}
};

export default ShareWithFormHandler;
